import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { api, ApiError } from '../../lib/api.js';
import { useApi } from '../../hooks/useApi.js';
import { useAuth } from '../../hooks/useAuth.jsx';
import { Button } from '../../components/Button.jsx';

const ORANGE = '#FF8A4C';

const CustomerModal = ({ open, customer, onClose, onSaved }) => {
  const editing = !!customer;
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(editing ? { name: customer.name ?? '', notes: customer.notes ?? '' } : { name: '', notes: '' });
      setError(null);
    }
  }, [open, editing, customer]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.currentTarget.value }));

  const save = async () => {
    if (!form.name?.trim()) return;
    setBusy(true); setError(null);
    try {
      const payload = { name: form.name.trim(), notes: form.notes?.trim() || null };
      const saved = editing
        ? await api.patch(`/customers/${customer.id}`, payload)
        : await api.post('/customers', payload);
      onSaved?.(saved);
    } catch (err) { setError(err); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!editing) return;
    const msg = (customer.profileCount || customer.readingCount)
      ? `Delete "${customer.name}" and ALL ${customer.profileCount} profile(s) + ${customer.readingCount} reading(s)? This cannot be undone.`
      : `Delete "${customer.name}"?`;
    if (!window.confirm(msg)) return;
    try { await api.delete(`/customers/${customer.id}`); onSaved?.(); }
    catch (err) { alert(err.message ?? 'Failed'); }
  };

  return (
    <div class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: ORANGE }}>{editing ? 'Edit customer' : 'New customer'}</p>
        </div>
        <div class="space-y-3 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Name</p>
            <input autoFocus type="text" value={form.name ?? ''} onInput={set('name')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </div>
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Notes</p>
            <textarea rows={3} value={form.notes ?? ''} onInput={set('notes')}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand" />
          </div>
          {editing && (
            <p class="text-[11px] text-text-tertiary">
              {customer.profileCount || 0} profile(s) · {customer.readingCount || 0} reading(s)
            </p>
          )}
          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed')}
            </p>
          )}
        </div>
        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {editing && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={busy || !form.name?.trim()}>
              {busy ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CustomersPage = () => {
  const { route } = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [search, setSearch] = useState('');
  const qs = search ? `?search=${encodeURIComponent(search)}` : '';
  const { data: customers, refetch } = useApi(`/customers${qs}`);
  const [editing, setEditing] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div>
      <section class="mb-5 flex items-start justify-between gap-4">
        <div>
          <div class="mb-1.5 flex items-center gap-2">
            <span class="block h-2.5 w-2.5 rounded-full" style={{ background: ORANGE }} />
            <span class="text-[11px] font-semibold uppercase tracking-eyebrow" style={{ color: ORANGE }}>Customers</span>
          </div>
          <h1 class="m-0 mb-1.5 text-[30px] leading-[1.18] tracking-tight text-text-primary">Customer directory</h1>
          <p class="m-0 text-sm text-text-secondary">Manage customers. Each customer holds press profiles + density readings.</p>
        </div>
        {isAdmin && <Button variant="primary" onClick={() => setNewOpen(true)}>+ New customer</Button>}
      </section>

      <div class="mb-4 flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onInput={(e) => setSearch(e.currentTarget.value)}
          placeholder="Search customers…"
          class="block w-72 max-w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 text-sm placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        />
        <span class="ml-auto text-[12px] text-text-tertiary">{customers ? `${customers.length} customer${customers.length === 1 ? '' : 's'}` : ''}</span>
      </div>

      {!customers && <p class="text-sm text-text-tertiary">Loading…</p>}
      {customers && customers.length === 0 && (
        <div class="surface-card p-6 text-center">
          <p class="m-0 font-serif text-lg font-medium text-text-primary">No customers.</p>
        </div>
      )}
      {customers && customers.length > 0 && (
        <div class="grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {customers.map((c) => (
            <div
              key={c.id}
              class="relative flex items-center justify-between gap-3 overflow-hidden rounded-md border border-border-subtle bg-bg-card p-3 transition hover:border-border-strong hover:bg-bg-hover"
            >
              <span class="pointer-events-none absolute inset-y-0 left-0 w-[3px]" style={{ background: ORANGE }} aria-hidden="true" />
              <button
                type="button"
                onClick={() => route(`/customer-profiles?customer=${c.id}`)}
                class="min-w-0 flex-1 text-left"
              >
                <p class="m-0 truncate font-serif text-[15px] font-medium text-text-primary">{c.name}</p>
                <p class="m-0 mt-0.5 text-[11px] text-text-tertiary">{c.profileCount} profile{c.profileCount === 1 ? '' : 's'} · {c.readingCount} reading{c.readingCount === 1 ? '' : 's'}</p>
              </button>
              {isAdmin && (
                <button onClick={() => setEditing(c)} class="text-[11px] text-text-tertiary hover:text-brand" aria-label="Edit">Edit</button>
              )}
            </div>
          ))}
        </div>
      )}

      <CustomerModal open={newOpen} onClose={() => setNewOpen(false)} onSaved={() => { setNewOpen(false); refetch(); }} />
      <CustomerModal open={!!editing} customer={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refetch(); }} />
    </div>
  );
};
